import React from 'react';
import { DollarSign, Zap, Heart, BarChart2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

export interface MetricsDisplayProps {
  totalCost: number;
  price: number;
  profit: number;
  techScore: number;
  valueScore?: number;
  userSatisfaction?: number;
  className?: string;
}

export function MetricsDisplay({
  totalCost,
  price,
  profit,
  techScore,
  valueScore,
  userSatisfaction,
  className,
}: MetricsDisplayProps) {
  const metrics = [
    {
      label: 'Cost',
      value: formatCurrency(totalCost),
      icon: DollarSign,
      color: 'text-theme-text-secondary',
    },
    {
      label: 'Price',
      value: formatCurrency(price),
      icon: BarChart2,
      color: 'text-theme-text-secondary',
    },
    {
      label: 'Profit',
      value: formatCurrency(profit),
      icon: DollarSign,
      color: profit >= 0 ? 'text-theme-success' : 'text-theme-error',
    },
    {
      label: 'Tech Score',
      value: techScore.toFixed(1).toString(),
      icon: Zap,
      color: 'text-theme-info',
    },
  ];

  if (valueScore !== undefined) {
    metrics.push({
      label: 'Value Score',
      value: valueScore.toString(),
      icon: BarChart2,
      color: 'text-theme-accent',
    });
  }

  if (userSatisfaction !== undefined) {
    metrics.push({
      label: 'User Score',
      value: userSatisfaction.toString(),
      icon: Heart,
      color:
        userSatisfaction >= 85
          ? 'text-theme-success'
          : userSatisfaction >= 70
          ? 'text-theme-warning'
          : 'text-theme-error',
    });
  }

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className={`grid gap-6 ${metrics.length <= 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
          {metrics.map((metric) => {
            const IconComponent = metric.icon;
            return (
              <div key={metric.label}>
                <div className="flex items-center gap-2 text-theme-text-muted mb-1">
                  <IconComponent className="w-4 h-4" />
                  <span className="text-sm">{metric.label}</span>
                </div>
                <p className={`text-lg font-semibold ${metric.color}`}>
                  {metric.value}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}